import { CatalogSite, Category, CategorySite, SiteOverride, SiteTier } from "../types";
import { ProtectedModel } from "./protectedModel";

/**
 * ENG-3139 — client-side arithmetic for site tier / tracked overrides.
 *
 * The catalog classifies every site once, globally (`CatalogSite.tier` / `.tracked`). An org or group
 * override moved a site away from that on either axis, in either direction.
 *
 * ENG-4055: the tier axis (promote / demote) is retired. The backend sends `tier: null` and ignores a
 * tier on a write, so the tier helpers below are no-ops against it; they still describe a backend
 * from before ENG-4055 truly. The console edits `tracked` only, and [withStoredTiers] sends a stored
 * tier back as read.
 */

const hasAxis = (o: SiteOverride) => (o.tier === "protected" || o.tier === "identity") || typeof o.tracked === "boolean";

const toCategorySite = (site: CatalogSite): CategorySite => ({
  slug: site.slug,
  title: site.title,
  launchUrl: site.launchUrl,
  clearbitLogoKey: site.clearbitLogoKey,
});

/**
 * Per axis: the override's value if it has one, else the site's own.
 * ENG-4044: remove `effectiveTier` with the old-backend compatibility code; a current backend sends
 * `tier: null`, so it is always the site's own.
 */
export function effectiveTier(site: CatalogSite, override?: SiteOverride | null): SiteTier {
  return override?.tier === "protected" || override?.tier === "identity" ? override.tier : site.tier;
}
export function effectiveTracked(site: CatalogSite, override?: SiteOverride | null): boolean {
  return typeof override?.tracked === "boolean" ? override.tracked : site.tracked;
}

/**
 * Group over tenant, PER AXIS — the same rule the backend resolves with (resolveSiteOverrides): a
 * group that only demotes a site leaves the org's `tracked` for it in force. Rows left with no axis
 * are dropped; the result is sorted by slug so it compares stably.
 */
export function mergeSiteOverrides(base: SiteOverride[], over: SiteOverride[]): SiteOverride[] {
  const bySlug = new Map<string, SiteOverride>();
  for (const b of base) bySlug.set(b.slug, { slug: b.slug, tier: b.tier ?? null, tracked: b.tracked ?? null });
  for (const o of over) {
    const b = bySlug.get(o.slug);
    bySlug.set(o.slug, {
      slug: o.slug,
      tier: o.tier === "protected" || o.tier === "identity" ? o.tier : b?.tier ?? null,
      tracked: typeof o.tracked === "boolean" ? o.tracked : b?.tracked ?? null,
    });
  }
  return Array.from(bySlug.values()).filter(hasAxis).sort((a, b) => a.slug.localeCompare(b.slug));
}

/**
 * ENG-3376: true when [a] and [b] set the same overrides, meaning the same axes on the same sites, in
 * any order. A row with neither axis set means "Haven's defaults", which the server stores as nothing,
 * so it counts as absent: adding a site and leaving it on Inherit changes nothing.
 */
export function sameSiteOverrides(a: SiteOverride[], b: SiteOverride[]): boolean {
  const x = mergeSiteOverrides([], a);
  const y = mergeSiteOverrides([], b);
  return x.length === y.length && x.every((o, i) => o.slug === y[i].slug && o.tier === y[i].tier && o.tracked === y[i].tracked);
}

/**
 * ENG-4055 — the write for a replace-all override list that the console edits on `tracked` alone.
 *
 * The PUT is replace-all: a slug left out loses both axes, and an absent `tier` is "no override", not
 * "unchanged". So a backend from before ENG-4055 would drop every stored tier. Each stored tier is sent
 * back as read instead, on the slug's entry or, for a slug the caller did not name, on an entry of its
 * own. A backend after ENG-4055 ignores `tier` and keeps the stored one, so the same write is right on
 * both. The same rule as account-mcp's `withStoredTiers` (ENG-4040). [keep] filters which slugs may be
 * sent at all (the server refuses one the catalog no longer lists).
 */
export function withStoredTiers(
  desired: Array<{ slug: string; tracked: boolean | null }>,
  stored: SiteOverride[] | null | undefined,
  keep: (slug: string) => boolean = () => true,
): SiteOverride[] {
  const tierBySlug = new Map<string, SiteTier>();
  for (const o of stored ?? []) if ((o.tier === "protected" || o.tier === "identity") && keep(o.slug)) tierBySlug.set(o.slug, o.tier);
  const named = new Set(desired.map((d) => d.slug));
  const out: SiteOverride[] = desired.map((d) => ({ slug: d.slug, tier: tierBySlug.get(d.slug) ?? null, tracked: d.tracked }));
  tierBySlug.forEach((tier, slug) => { if (!named.has(slug)) out.push({ slug, tier, tracked: null }); });
  return out;
}

/**
 * An empty bucket has nothing to lock, make Protected or enable; an ordinary empty category stays,
 * since locking it covers sites added later. Run after the overrides (ENG-3174).
 *
 * ENG-4033: under model 2 any category can be Protected (Sean, 2026-10-26), the Verified-only buckets
 * (Education, Miscellaneous) included: turning one on, locking it or adding it makes its rows
 * Protected. So nothing is dropped there, and they get a toggle and a lock like every other category.
 */
export function withProtectableSites(categories: Category[], model: ProtectedModel = 1): Category[] {
  if (model === 2) return categories;
  // ENG-4044: remove model-1 branch after the flip
  return categories.filter((c) => !c.identityOnly || c.sites.length > 0);
}

/**
 * The categories as the locked/denied editor should list them under [overrides]: promoted
 * identity-only sites join their category's site list, demoted sites leave it. Returns the input
 * untouched when nothing changes tier, so callers keyed on identity re-render only when needed.
 */
// ENG-4044: remove with the old-backend compatibility code; a no-op against a backend after ENG-4055.
export function applyTierOverridesToCategories(
  categories: Category[],
  catalogSites: CatalogSite[] | null | undefined,
  overrides: SiteOverride[] | null | undefined,
): Category[] {
  const tierOverrides = new Map<string, SiteTier>();
  for (const o of overrides ?? []) if (o.tier === "protected" || o.tier === "identity") tierOverrides.set(o.slug, o.tier);
  if (tierOverrides.size === 0) return categories;
  const catalogBySlug = new Map((catalogSites ?? []).map((s) => [s.slug, s]));
  return categories.map((c) => {
    const kept = c.sites.filter((s) => tierOverrides.get(s.slug) !== "identity");
    const promoted: CategorySite[] = [];
    tierOverrides.forEach((tier, slug) => {
      if (tier !== "protected") return;
      const site = catalogBySlug.get(slug);
      // Only a site Haven lists as identity-only in THIS category is new here; a protected row is
      // already in `c.sites`, and a slug the catalog does not carry cannot be rendered.
      if (!site || site.category !== c.slug || site.tier !== "identity") return;
      if (kept.some((s) => s.slug === slug)) return;
      promoted.push(toCategorySite(site));
    });
    if (promoted.length === 0 && kept.length === c.sites.length) return c;
    const sites = [...kept, ...promoted].sort((a, b) => a.title.localeCompare(b.title));
    return { ...c, sites, siteCount: sites.length };
  });
}

/**
 * ENG-4033 — a category with the rows Haven lists as Verified only (tier `identity`) added to its site
 * list, by title. `/categories` lists protected-tier rows only; under model 2 a lock or a Protected
 * tick reaches every row, so the editor lists every row. Unchanged when the catalog is not loaded.
 */
export function withVerifiedRows(c: Category, catalogSites: CatalogSite[] | null | undefined): Category {
  if (!catalogSites) return c;
  const listed = new Set(c.sites.map((s) => s.slug));
  const extra: CategorySite[] = catalogSites
    .filter((s) => s.category === c.slug && s.tier === "identity" && !listed.has(s.slug))
    .map((s) => ({ slug: s.slug, title: s.title, launchUrl: s.launchUrl, clearbitLogoKey: s.clearbitLogoKey }));
  if (extra.length === 0) return c;
  const sites = [...c.sites, ...extra].sort((a, b) => a.title.localeCompare(b.title));
  return { ...c, sites, siteCount: sites.length };
}
